import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { phoneLoginConfig, type LoginMethodConfig } from "@platform/core";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Chip,
  Dialog,
  Field,
  Input,
  NativeSelect,
  Skeleton,
  Switch,
} from "@platform/ui";
import { useApi, useConsole } from "../context";
import { useT, type MessageKey } from "../i18n";
import { SettingErrors, useSetting } from "../lib/use-setting";
import { ErrorBanner, PageCard, Section, TableShell, td, th } from "../layout/Page";

type ProviderId = "emailPassword" | "google" | "microsoft" | "passkey";
const PROVIDERS: Array<{ id: ProviderId; label: MessageKey }> = [
  { id: "emailPassword", label: "signin.emailPassword" },
  { id: "google", label: "signin.google" },
  { id: "microsoft", label: "signin.microsoft" },
  { id: "passkey", label: "signin.passkey" },
];

function isEnabled(cfg: LoginMethodConfig, id: ProviderId): boolean {
  if (id === "emailPassword") return cfg.emailPassword.enabled;
  if (id === "passkey") return cfg.passkey.enabled;
  return cfg.social[id]?.enabled ?? false;
}

function EmailPasswordEditor({
  value,
  onChange,
}: {
  value: LoginMethodConfig["emailPassword"];
  onChange: (v: LoginMethodConfig["emailPassword"]) => void;
}) {
  const { t } = useT();
  const [domain, setDomain] = useState("");
  const addDomain = () => {
    const d = domain.trim().toLowerCase().replace(/^@/, "");
    if (d && !value.allowedEmailDomains.includes(d))
      onChange({ ...value, allowedEmailDomains: [...value.allowedEmailDomains, d] });
    setDomain("");
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{t("signin.enable")}</span>
        <Switch
          label={t("signin.enable")}
          checked={value.enabled}
          onCheckedChange={(v) => onChange({ ...value, enabled: v })}
        />
      </div>
      <Checkbox
        label={t("signin.allowSignUp")}
        checked={value.allowSignUp}
        onChange={(e) => onChange({ ...value, allowSignUp: e.target.checked })}
      />
      <Checkbox
        label={t("signin.requireVerification")}
        checked={value.requireEmailVerification}
        onChange={(e) => onChange({ ...value, requireEmailVerification: e.target.checked })}
      />
      <div>
        <Field label={t("signin.domains")} hint={t("signin.domainsHint")} className="mb-2">
          {(p) => (
            <div className="flex gap-2">
              <Input
                {...p}
                value={domain}
                placeholder="company.co.th"
                onChange={(e) => setDomain(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addDomain();
                  }
                }}
              />
              <Button variant="secondary" onClick={addDomain}>
                {t("signin.addDomain")}
              </Button>
            </div>
          )}
        </Field>
        <div className="flex flex-wrap gap-2">
          {value.allowedEmailDomains.map((d) => (
            <Chip
              key={d}
              removeLabel={`${t("common.remove")} ${d}`}
              onRemove={() =>
                onChange({ ...value, allowedEmailDomains: value.allowedEmailDomains.filter((x) => x !== d) })
              }
            >
              {d}
            </Chip>
          ))}
        </div>
      </div>
      <Field label={t("signin.minLength")}>
        {(p) => (
          <Input
            {...p}
            type="number"
            min={8}
            max={128}
            value={value.minPasswordLength}
            onChange={(e) => onChange({ ...value, minPasswordLength: Number(e.target.value) })}
          />
        )}
      </Field>
    </div>
  );
}

export function SignInMethodPage() {
  const { t } = useT();
  const api = useApi();
  const { can } = useConsole();
  const s = useSetting("login-methods");
  const providers = useQuery({ queryKey: ["sign-in-providers"], queryFn: () => api.settings.providers() });
  const [editing, setEditing] = useState<ProviderId | null>(null);
  const [edit, setEdit] = useState<LoginMethodConfig | null>(null);
  const editable = can("settings:write");
  if (s.query.isError)
    return (
      <PageCard>
        <ErrorBanner error={s.query.error} onRetry={() => void s.query.refetch()} />
      </PageCard>
    );
  const cfg = s.draft;
  if (!cfg)
    return (
      <PageCard>
        <Skeleton className="m-6 h-64" />
      </PageCard>
    );
  const status = (id: ProviderId) => providers.data?.items.find((p) => p.id === id);
  const openEditor = (id: ProviderId) => {
    setEdit(structuredClone(cfg));
    setEditing(id);
  };
  const applyEditor = () => {
    if (edit) {
      s.setDraft(edit);
      s.save.mutate(edit);
    }
    setEditing(null);
  };
  const setMethods = (m: "totp" | "email_otp", on: boolean) =>
    s.setDraft({
      ...cfg,
      mfa: {
        ...cfg.mfa,
        methods: on ? [...new Set([...cfg.mfa.methods, m])] : cfg.mfa.methods.filter((x) => x !== m),
      },
    });

  return (
    <div className="mx-auto flex max-w-[var(--container-content)] flex-col gap-6">
      <PageCard>
        <SettingErrors state={s} conflictLabel={t("signin.conflict")} />
        <TableShell label={t("nav.signInMethod")}>
          <thead>
            <tr>
              <th className={th}>{t("signin.provider")}</th>
              <th className={th}>{t("signin.status")}</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {PROVIDERS.map((p) => {
              const st = status(p.id);
              const unsupported = p.id === "passkey" && st?.supported === false;
              const notConfigured = (p.id === "google" || p.id === "microsoft") && st && !st.configured;
              return (
                <tr key={p.id} data-testid={`provider-${p.id}`}>
                  <td className={td}>
                    <p className="font-medium">{t(p.label)}</p>
                    {notConfigured && (
                      <p className="mt-1 text-xs text-warning">
                        {t("signin.notConfigured", { provider: t(p.label) })}
                      </p>
                    )}
                  </td>
                  <td className={td}>
                    {unsupported ? (
                      <Badge>{t("signin.unsupported")}</Badge>
                    ) : (
                      <Badge dot tone={isEnabled(cfg, p.id) ? "success" : "neutral"}>
                        {isEnabled(cfg, p.id) ? t("common.enabled") : t("common.disabled")}
                      </Badge>
                    )}
                  </td>
                  <td className={`${td} text-right`}>
                    {editable && !unsupported && (
                      <Button
                        variant="text"
                        size="sm"
                        onClick={() => openEditor(p.id)}
                        aria-label={`${t("common.edit")} ${t(p.label)}`}
                      >
                        {t("common.edit")}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TableShell>
      </PageCard>

      <PageCard>
        <Section title={t("signin.mfa")}>
          <Field label={t("signin.mfaPolicy")}>
            {(p) => (
              <NativeSelect
                {...p}
                disabled={!editable}
                value={cfg.mfa.policy}
                onChange={(e) =>
                  s.setDraft({
                    ...cfg,
                    mfa: { ...cfg.mfa, policy: e.target.value as LoginMethodConfig["mfa"]["policy"] },
                  })
                }
              >
                {(["optional", "required_for_admins", "required_for_all"] as const).map((v) => (
                  <option key={v} value={v}>
                    {t(`signin.policy.${v}`)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <fieldset>
            <legend className="mb-2 text-sm font-medium">{t("signin.methods")}</legend>
            <div className="space-y-2">
              <Checkbox
                label={t("signin.method.totp")}
                disabled={!editable}
                checked={cfg.mfa.methods.includes("totp")}
                onChange={(e) => setMethods("totp", e.target.checked)}
              />
              <Checkbox
                label={t("signin.method.email_otp")}
                disabled={!editable}
                checked={cfg.mfa.methods.includes("email_otp")}
                onChange={(e) => setMethods("email_otp", e.target.checked)}
              />
            </div>
          </fieldset>
        </Section>
        <Section title={t("signin.phone")}>
          {providers.data?.items.find((p) => p.id === "sms")?.configured === false && (
            <Alert tone="warning" className="mb-3">
              {t("signin.smsMissing")}
            </Alert>
          )}
          <div className="space-y-2">
            <Checkbox
              label={t("signin.phoneEnabled")}
              disabled={!editable}
              checked={phoneLoginConfig(cfg).enabled}
              onChange={(e) =>
                s.setDraft({ ...cfg, phone: { ...phoneLoginConfig(cfg), enabled: e.target.checked } })
              }
            />
            <Checkbox
              label={t("signin.phoneOtp")}
              disabled={!editable || !phoneLoginConfig(cfg).enabled}
              checked={phoneLoginConfig(cfg).enabled && phoneLoginConfig(cfg).otpSignIn}
              onChange={(e) =>
                s.setDraft({ ...cfg, phone: { ...phoneLoginConfig(cfg), otpSignIn: e.target.checked } })
              }
            />
          </div>
        </Section>
        <Section title={t("signin.session")}>
          <div className="grid gap-x-4 sm:grid-cols-2">
            <Field label={t("signin.maxAge")}>
              {(p) => (
                <Input
                  {...p}
                  type="number"
                  min={1}
                  max={365}
                  disabled={!editable}
                  value={cfg.session.maxAgeDays}
                  onChange={(e) =>
                    s.setDraft({ ...cfg, session: { ...cfg.session, maxAgeDays: Number(e.target.value) } })
                  }
                />
              )}
            </Field>
            <Field label={t("signin.idle")}>
              {(p) => (
                <Input
                  {...p}
                  type="number"
                  min={5}
                  disabled={!editable}
                  value={cfg.session.idleTimeoutMinutes ?? ""}
                  onChange={(e) => {
                    const { idleTimeoutMinutes: _drop, ...rest } = cfg.session;
                    s.setDraft({
                      ...cfg,
                      session: e.target.value
                        ? { ...rest, idleTimeoutMinutes: Number(e.target.value) }
                        : rest,
                    });
                  }}
                />
              )}
            </Field>
          </div>
        </Section>
        {editable && (
          <div className="flex justify-end px-6 pb-5">
            <Button
              data-testid="save-settings-button"
              loading={s.save.isPending}
              onClick={() => s.save.mutate(cfg)}
            >
              {t("signin.save")}
            </Button>
          </div>
        )}
      </PageCard>

      <Dialog
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        title={
          editing
            ? t("signin.editTitle", { provider: t(PROVIDERS.find((p) => p.id === editing)!.label) })
            : ""
        }
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              {t("common.cancel")}
            </Button>
            <Button data-testid="save-provider-button" onClick={applyEditor}>
              {t("common.save")}
            </Button>
          </>
        }
      >
        {edit && editing === "emailPassword" && (
          <EmailPasswordEditor
            value={edit.emailPassword}
            onChange={(v) => setEdit({ ...edit, emailPassword: v })}
          />
        )}
        {edit && (editing === "google" || editing === "microsoft") && (
          <div className="space-y-4">
            {status(editing)?.configured === false && (
              <Alert tone="warning">
                {t("signin.notConfigured", {
                  provider: t(editing === "google" ? "signin.google" : "signin.microsoft"),
                })}
              </Alert>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t("signin.enable")}</span>
              <Switch
                label={t("signin.enable")}
                checked={edit.social[editing]?.enabled ?? false}
                onCheckedChange={(v) =>
                  setEdit({ ...edit, social: { ...edit.social, [editing]: { enabled: v } } })
                }
              />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
